// src/pages/CandidateDashboard.tsx: candidate's own hub
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  C, Candidate, TalentConnection, TalentMessage,
  uploadCV, signedCVUrl, parseCVWithAI, isCandidateComplete, seniorityLabel,
} from '../lib/talentConnect';
import { card, inputStyle, labelStyle, btnPrimary, btnGhost, Banner, LoadingScreen, fmtDate } from '../lib/talentUI';
import { CandidateFields, CandidateFormState, candidatePayload } from '../lib/CandidateFields';
import { supabase } from '../services/supabase';
import MarketContext from '../components/MarketContext';

type Tab = 'overview' | 'profile' | 'cv' | 'connections' | 'settings';

export default function CandidateDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [loading, setLoading]     = useState(true);

  // 'overview' is the param-less landing tab so the sidebar's Dashboard link
  // (no ?tab=) stays highlighted while it is active.
  const tab = (searchParams.get('tab') as Tab) || 'overview';
  const setTab = (t: Tab) => setSearchParams(t === 'overview' ? {} : { tab: t });

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase.from('candidates').select('*').eq('owner_id', user.id).maybeSingle();
      if (!data) { navigate('/candidate-onboarding', { replace: true }); return; }
      setCandidate(data as Candidate);
      setLoading(false);
    })();
  }, [user, navigate]);

  if (loading || !candidate) return <LoadingScreen label="Loading your dashboard…" />;

  const tabs: [Tab, string][] = [['overview', 'Dashboard'], ['profile', 'Profile'], ['cv', 'My CV'], ['connections', 'Connections'], ['settings', 'Settings']];

  return (
    <div style={{ padding: '28px 32px', minHeight: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* The overview tab leads with its own welcome card, so the page title
          would only repeat it. */}
      {tab !== 'overview' && (
        <div style={{ marginBottom: '18px' }}>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.black, fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px' }}>My TalentConnect Profile</h1>
          <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>Manage your profile, CV, connections and visibility</p>
        </div>
      )}

      {!candidate.profile_complete && tab !== 'overview' && (
        <Banner type="info" text="Your profile is incomplete. Add your name, current role, seniority and at least one target role so managers can find you." />
      )}

      <div style={{ display: 'flex', gap: '2px', borderBottom: `1px solid ${C.border}`, marginBottom: '22px', flexWrap: 'wrap' }}>
        {tabs.map(([key, lbl]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '10px 16px',
            fontSize: '13px', fontFamily: 'system-ui', fontWeight: tab === key ? 600 : 500,
            color: tab === key ? C.navy : C.muted,
            borderBottom: tab === key ? `2px solid ${C.navy}` : '2px solid transparent', marginBottom: '-1px',
          }}>{lbl}</button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab candidate={candidate} userId={user!.id} onTab={setTab} />}
      {tab === 'profile' && <ProfileTab candidate={candidate} onSaved={setCandidate} />}
      {tab === 'cv' && <CVTab candidate={candidate} onSaved={setCandidate} />}
      {tab === 'connections' && <ConnectionsTab candidateId={candidate.id} userId={user!.id} />}
      {tab === 'settings' && <SettingsTab candidate={candidate} onSaved={setCandidate} />}
    </div>
  );
}

// ── Overview tab ─────────────────────────────────────────────────────────────
// Fields that make a candidate findable. Order is the order they are listed
// back as "still missing".
const COMPLETION_FIELDS: [keyof Candidate, string][] = [
  ['full_name',        'Full name'],
  ['current_role',     'Current role'],
  ['description',      'Summary'],
  ['years_experience', 'Years of experience'],
  ['seniority',        'Seniority'],
  ['target_roles',     'Target roles'],
  ['asset_classes',    'Asset classes'],
  ['skills',           'Skills'],
  ['cv_url',           'CV'],
];

function isFilled(c: Candidate, key: keyof Candidate): boolean {
  const v = c[key];
  if (v == null) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'string') return v.trim().length > 0;
  return true;
}

const STATUS_STYLE: Record<string, { label: string; color: string; bg: string; bd: string }> = {
  pending:  { label: 'Pending',  color: C.amber,   bg: '#FFFBEB', bd: '#FDE68A' },
  accepted: { label: 'Accepted', color: C.success, bg: '#F0FDF4', bd: '#BBF7D0' },
  declined: { label: 'Declined', color: C.danger,  bg: '#FEF2F2', bd: '#FECACA' },
};

export function StatusBadge({ status }: { status: string }) {
  const s = STATUS_STYLE[status] || { label: status, color: C.muted, bg: C.surface, bd: C.border };
  return (
    <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.bd}`, fontSize: '9px', fontWeight: 700, padding: '2px 7px', borderRadius: '2px', letterSpacing: '0.5px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{s.label}</span>
  );
}

function StatCard({ label, value, accent, note }: { label: string; value: number | null; accent: string; note?: string }) {
  return (
    <div style={{ ...card, padding: '16px 18px', borderLeft: `3px solid ${accent}` }}>
      <div style={{ ...labelStyle, marginBottom: '8px' }}>{label}</div>
      <div style={{ fontFamily: 'Georgia, serif', fontSize: '28px', color: accent, lineHeight: 1, letterSpacing: '-0.5px', marginBottom: '5px' }}>
        {value == null ? '-' : value}
      </div>
      <div style={{ fontSize: '11px', color: C.mutedLt }}>{note || ' '}</div>
    </div>
  );
}

interface ActivityRow extends TalentConnection {
  job: { title: string } | null;
  manager_firm: { name: string } | null;
}

function OverviewTab({ candidate, userId, onTab }: {
  candidate: Candidate; userId: string; onTab: (t: Tab) => void;
}) {
  const navigate = useNavigate();
  const [counts, setCounts]   = useState<{ total: number; pending: number; accepted: number } | null>(null);
  const [recent, setRecent]   = useState<ActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      const base = () => supabase.from('talent_connections').select('*', { count: 'exact', head: true }).eq('candidate_id', candidate.id);
      const [total, pending, accepted, recentRes] = await Promise.all([
        base(),
        base().eq('status', 'pending'),
        base().eq('status', 'accepted'),
        supabase.from('talent_connections')
          .select('*, job:talent_jobs(title), manager_firm:firms(name)')
          .eq('candidate_id', candidate.id).order('requested_at', { ascending: false }).limit(5),
      ]);
      if (cancelled) return;
      const failed = [total, pending, accepted, recentRes].find(r => r.error);
      if (failed?.error) { setError(failed.error.message); setLoading(false); return; }
      setCounts({ total: total.count || 0, pending: pending.count || 0, accepted: accepted.count || 0 });
      setRecent((recentRes.data as unknown as ActivityRow[]) || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [candidate.id]);

  const missing = COMPLETION_FIELDS.filter(([k]) => !isFilled(candidate, k));
  const pct = Math.round(((COMPLETION_FIELDS.length - missing.length) / COMPLETION_FIELDS.length) * 100);
  const firstName = (candidate.full_name || '').trim().split(' ')[0];
  // hide_employer is the candidate's own privacy switch, so respect it here too.
  const roleLine = [candidate.current_role, candidate.hide_employer ? null : candidate.current_employer]
    .filter(Boolean).join(' · ');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '1100px' }}>

      {/* ── Welcome ── */}
      <div style={{ background: C.navy, borderRadius: '4px', padding: '24px 28px', borderLeft: `3px solid ${C.gold}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '9.5px', color: C.gold, letterSpacing: '1.6px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '8px' }}>
              Your TalentConnect Dashboard
            </div>
            <h1 style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '25px', color: C.white, fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px' }}>
              Welcome back{firstName ? `, ${firstName}` : ''}
            </h1>
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)' }}>{roleLine || 'Add your current role to your profile'}</div>
          </div>
          <span style={{ background: 'rgba(201,168,76,0.14)', color: C.gold, border: `1px solid rgba(201,168,76,0.35)`, padding: '3px 10px', borderRadius: '2px', fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            {seniorityLabel(candidate.seniority)}
          </span>
        </div>
      </div>

      {error && <Banner type="err" text={error} />}

      {/* ── Metrics, 4 across, 2x2 on narrow screens ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '12px' }}>
        <StatCard label="Profile Views"        value={0} accent={C.muted} note="Coming soon" />
        <StatCard label="Connection Requests"  value={loading ? null : counts?.total ?? 0} accent={C.navy} note="All time" />
        <StatCard label="Awaiting Response"    value={loading ? null : counts?.pending ?? 0}
          accent={!loading && (counts?.pending || 0) > 0 ? C.gold : C.muted}
          note={!loading && (counts?.pending || 0) > 0 ? 'Needs your reply' : 'Nothing waiting'} />
        <StatCard label="Active Conversations" value={loading ? null : counts?.accepted ?? 0} accent={C.success} note="Accepted connections" />
      </div>

      {/* ── Market context ── */}
      <MarketContext />

      {/* ── Profile completion ── */}
      {pct < 100 && (
        <div style={{ ...card, padding: '20px 24px', borderLeft: `3px solid ${C.gold}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: C.navy, marginBottom: '2px' }}>Complete your profile to appear in manager searches</div>
              <div style={{ fontSize: '12px', color: C.muted }}>{missing.length} field{missing.length !== 1 ? 's' : ''} still to add</div>
            </div>
            <div style={{ fontFamily: 'Georgia, serif', fontSize: '22px', color: C.gold, lineHeight: 1 }}>{pct}%</div>
          </div>
          <div style={{ height: '5px', background: C.borderLt, borderRadius: '3px', overflow: 'hidden', marginBottom: '12px' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: C.gold, transition: 'width 0.25s ease' }} />
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '14px' }}>
            {missing.map(([, lbl]) => (
              <span key={lbl} style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.textSec, fontSize: '11.5px', fontWeight: 500, padding: '3px 9px', borderRadius: '2px' }}>{lbl}</span>
            ))}
          </div>
          <button onClick={() => onTab(missing.some(([k]) => k === 'cv_url') && missing.length === 1 ? 'cv' : 'profile')} style={{ ...btnGhost, padding: '7px 14px' }}>
            Complete profile →
          </button>
        </div>
      )}

      {/* ── Recent activity ── */}
      <div style={card}>
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: C.black }}>Recent Activity</div>
            <div style={{ fontSize: '11px', color: C.muted, marginTop: '1px' }}>Your five most recent connections</div>
          </div>
          <button onClick={() => onTab('connections')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: C.navy, fontWeight: 600, letterSpacing: '0.2px', fontFamily: 'system-ui' }}>VIEW ALL →</button>
        </div>
        {loading ? (
          <div style={{ padding: '36px', textAlign: 'center', fontSize: '13px', color: C.muted }}>Loading activity…</div>
        ) : recent.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: C.text, marginBottom: '6px' }}>No connection requests yet</div>
            <div style={{ fontSize: '13px', color: C.muted, maxWidth: '380px', margin: '0 auto', lineHeight: 1.6 }}>
              Make sure your profile is visible to attract manager interest.
            </div>
          </div>
        ) : recent.map((r, i) => (
          <div key={r.id} style={{
            display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 20px',
            borderBottom: i < recent.length - 1 ? `1px solid ${C.borderLt}` : 'none', flexWrap: 'wrap',
          }}>
            <div style={{ width: '30px', height: '30px', borderRadius: '3px', background: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.gold, fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: '13px', flexShrink: 0 }}>
              {(r.manager_firm?.name || '?').charAt(0)}
            </div>
            <div style={{ flex: 1, minWidth: '150px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: C.black }}>{r.manager_firm?.name || 'A firm'}</div>
              <div style={{ fontSize: '11px', color: C.muted, marginTop: '1px' }}>
                {r.job?.title || 'a role'} · {r.requested_by === userId ? 'You applied' : 'Requested'} {fmtDate(r.requested_at)}
              </div>
            </div>
            <StatusBadge status={r.status} />
            <button onClick={() => onTab('connections')} style={{ ...btnGhost, padding: '5px 12px', flexShrink: 0 }}>View</button>
          </div>
        ))}
      </div>

      {/* ── Quick actions ── */}
      <div style={{ ...card, padding: '18px 20px' }}>
        <div style={{ ...labelStyle, marginBottom: '12px' }}>Quick Actions</div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button onClick={() => onTab('profile')} style={btnPrimary}>Edit Profile</button>
          <button onClick={() => navigate(`/talent-connect/candidate/${candidate.id}`)} style={btnGhost}>View My Profile</button>
          <button onClick={() => onTab('settings')} style={btnGhost}>Update Visibility</button>
        </div>
      </div>
    </div>
  );
}

// ── Profile tab ──────────────────────────────────────────────────────────────
function candidateToForm(c: Candidate): CandidateFormState {
  return {
    full_name: c.full_name || '', current_role: c.current_role || '', current_employer: c.current_employer || '',
    hide_employer: c.hide_employer, description: c.description || '',
    years_experience: c.years_experience != null ? String(c.years_experience) : '',
    seniority: (c.seniority as any) || 'mid',
    target_roles: c.target_roles || [], asset_classes: c.asset_classes || [],
    skills: c.skills || [], relocation_countries: c.relocation_countries || [],
  };
}

function ProfileTab({ candidate, onSaved }: { candidate: Candidate; onSaved: (c: Candidate) => void }) {
  const [form, setForm] = useState<CandidateFormState>(candidateToForm(candidate));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const save = async () => {
    setSaving(true); setMsg(null);
    const payload = candidatePayload(form);
    const { data, error } = await supabase.from('candidates').update({
      ...payload,
      profile_complete: isCandidateComplete(payload),
      updated_at: new Date().toISOString(),
    }).eq('id', candidate.id).select().single();
    setSaving(false);
    if (error) { setMsg({ type: 'err', text: error.message }); return; }
    onSaved(data as Candidate);
    setMsg({ type: 'ok', text: 'Profile saved.' });
  };

  return (
    <div style={{ ...card, padding: '26px 28px', maxWidth: '820px' }}>
      {msg && <Banner type={msg.type} text={msg.text} />}
      <CandidateFields form={form} setForm={setForm} />
      <div style={{ marginTop: '24px' }}>
        <button onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1, padding: '10px 26px' }}>{saving ? 'Saving…' : 'Save Profile'}</button>
      </div>
    </div>
  );
}

// ── CV tab ───────────────────────────────────────────────────────────────────
function CVTab({ candidate, onSaved }: { candidate: Candidate; onSaved: (c: Candidate) => void }) {
  const { user } = useAuth();
  const [cvUrl, setCvUrl]     = useState<string | null>(null);
  const [busy, setBusy]       = useState(false);
  const [msg, setMsg]         = useState<{ type: 'ok' | 'err' | 'info'; text: string } | null>(null);
  const [extracted, setExtracted] = useState<Partial<CandidateFormState> | null>(null);
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { (async () => setCvUrl(await signedCVUrl(candidate.cv_url)))(); }, [candidate.cv_url]);

  const handleFile = async (file: File) => {
    if (!user) return;
    if (file.type !== 'application/pdf') { setMsg({ type: 'err', text: 'Please upload a PDF.' }); return; }
    setBusy(true); setMsg(null); setExtracted(null);
    try {
      const [path, parsed] = await Promise.all([uploadCV(user.id, file), parseCVWithAI(file)]);
      setPendingPath(path);
      setExtracted({
        current_role: parsed.current_role, current_employer: parsed.current_employer,
        description: parsed.description, years_experience: String(parsed.years_experience),
        seniority: parsed.seniority, target_roles: parsed.target_roles,
        asset_classes: parsed.asset_classes, skills: parsed.skills,
        relocation_countries: parsed.relocation_countries,
      });
      setMsg({ type: 'info', text: 'CV uploaded and analysed. Review the extracted fields below, then apply the ones you want.' });
    } catch (e: any) {
      // If the upload itself worked we still store the CV path.
      setMsg({ type: 'err', text: `CV uploaded but AI extraction failed: ${e.message || 'unavailable'}.` });
      try { if (!pendingPath) setPendingPath(await uploadCV(user.id, file)); } catch { /* ignore */ }
    }
    setBusy(false);
  };

  // Just save the CV path (no field changes).
  const saveCvOnly = async () => {
    if (!pendingPath) return;
    const { data } = await supabase.from('candidates').update({ cv_url: pendingPath, updated_at: new Date().toISOString() }).eq('id', candidate.id).select().single();
    if (data) { onSaved(data as Candidate); setPendingPath(null); setExtracted(null); setMsg({ type: 'ok', text: 'CV saved.' }); }
  };

  // Apply the extracted fields to the candidate profile + save CV.
  const applyExtracted = async () => {
    if (!extracted) return;
    setBusy(true);
    const merged = {
      current_role: extracted.current_role || candidate.current_role,
      current_employer: extracted.current_employer || candidate.current_employer,
      description: extracted.description || candidate.description,
      years_experience: extracted.years_experience ? parseInt(extracted.years_experience) : candidate.years_experience,
      seniority: extracted.seniority || candidate.seniority,
      target_roles: extracted.target_roles?.length ? extracted.target_roles : candidate.target_roles,
      asset_classes: extracted.asset_classes?.length ? extracted.asset_classes : candidate.asset_classes,
      skills: extracted.skills?.length ? extracted.skills : candidate.skills,
      relocation_countries: extracted.relocation_countries?.length ? extracted.relocation_countries : candidate.relocation_countries,
    };
    const { data, error } = await supabase.from('candidates').update({
      ...merged,
      cv_url: pendingPath || candidate.cv_url,
      profile_complete: isCandidateComplete({ ...candidate, ...merged } as any),
      updated_at: new Date().toISOString(),
    }).eq('id', candidate.id).select().single();
    setBusy(false);
    if (error) { setMsg({ type: 'err', text: error.message }); return; }
    onSaved(data as Candidate); setPendingPath(null); setExtracted(null);
    setMsg({ type: 'ok', text: 'CV and profile updated from your CV.' });
  };

  const fieldRow = (label: string, value: any) => {
    const display = Array.isArray(value) ? value.join(', ') : String(value ?? '');
    if (!display) return null;
    return (
      <div key={label} style={{ display: 'flex', gap: '12px', padding: '9px 0', borderBottom: `1px solid ${C.borderLt}` }}>
        <div style={{ width: '150px', flexShrink: 0, fontSize: '11px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
        <div style={{ fontSize: '13px', color: C.text, lineHeight: 1.5 }}>{display}</div>
      </div>
    );
  };

  return (
    <div style={{ maxWidth: '760px' }}>
      <div style={{ ...card, padding: '24px 26px', marginBottom: '18px' }}>
        {msg && <Banner type={msg.type} text={msg.text} />}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontFamily: 'Georgia, serif', fontSize: '16px', color: C.navy, marginBottom: '4px' }}>Your CV</div>
            <div style={{ fontSize: '12.5px', color: C.muted }}>
              {candidate.cv_url ? 'A CV is on file.' : 'No CV uploaded yet.'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {cvUrl && <a href={cvUrl} target="_blank" rel="noreferrer" style={{ ...btnGhost, textDecoration: 'none', display: 'inline-block' }}>Download CV</a>}
            <button onClick={() => fileRef.current?.click()} disabled={busy} style={{ ...btnPrimary, opacity: busy ? 0.7 : 1 }}>
              {busy ? 'Working…' : candidate.cv_url ? 'Re-upload CV' : 'Upload CV'}
            </button>
          </div>
        </div>
        <input ref={fileRef} type="file" accept="application/pdf" style={{ display: 'none' }}
          onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }} />
      </div>

      {extracted && (
        <div style={{ ...card, padding: '24px 26px' }}>
          <div style={{ fontFamily: 'Georgia, serif', fontSize: '16px', color: C.navy, marginBottom: '4px' }}>Extracted from your CV</div>
          <p style={{ fontSize: '12.5px', color: C.muted, margin: '0 0 14px', lineHeight: 1.6 }}>Apply these to your profile, or keep just the CV file. You can fine-tune anything in the Profile tab.</p>
          <div style={{ marginBottom: '18px' }}>
            {fieldRow('Current Role', extracted.current_role)}
            {fieldRow('Current Employer', extracted.current_employer)}
            {fieldRow('Years Experience', extracted.years_experience)}
            {fieldRow('Seniority', extracted.seniority)}
            {fieldRow('Summary', extracted.description)}
            {fieldRow('Target Roles', extracted.target_roles)}
            {fieldRow('Asset Classes', extracted.asset_classes)}
            {fieldRow('Skills', extracted.skills)}
            {fieldRow('Relocation', extracted.relocation_countries)}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={applyExtracted} disabled={busy} style={{ ...btnPrimary, opacity: busy ? 0.7 : 1 }}>Apply to my profile</button>
            <button onClick={saveCvOnly} disabled={busy} style={btnGhost}>Keep CV only</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Connections tab (with messaging) ─────────────────────────────────────────
interface ConnRow extends TalentConnection {
  job: { title: string } | null;
  manager_firm: { name: string } | null;
}

function ConnectionsTab({ candidateId, userId }: { candidateId: string; userId: string }) {
  const [rows, setRows]   = useState<ConnRow[]>([]);
  const [loading, setLoad] = useState(true);
  const [busy, setBusy]   = useState<string | null>(null);
  const [active, setActive] = useState<ConnRow | null>(null);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [candidateId]);
  async function load() {
    setLoad(true);
    const { data } = await supabase.from('talent_connections')
      .select('*, job:talent_jobs(title), manager_firm:firms(name)')
      .eq('candidate_id', candidateId).order('requested_at', { ascending: false });
    setRows((data as unknown as ConnRow[]) || []);
    setLoad(false);
  }

  const respond = async (id: string, accept: boolean) => {
    setBusy(id);
    await supabase.from('talent_connections').update({
      status: accept ? 'accepted' : 'declined', responded_at: new Date().toISOString(),
    }).eq('id', id);
    setBusy(null); load();
  };

  if (loading) return <div style={{ color: C.muted, fontSize: '13px' }}>Loading connections…</div>;

  // A row the candidate created themselves is an outbound job-board
  // application: the manager responds to it, so it must not be offered back to
  // the candidate with Accept / Decline buttons.
  const isOutbound = (r: ConnRow) => r.requested_by === userId;
  const pending  = rows.filter(r => r.status === 'pending' && !isOutbound(r));
  const applied  = rows.filter(r => r.status === 'pending' && isOutbound(r));
  const accepted = rows.filter(r => r.status === 'accepted');

  return (
    <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 420px', minWidth: '360px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <div>
          <div style={{ ...labelStyle, marginBottom: '12px' }}>Pending Requests ({pending.length})</div>
          {pending.length === 0 ? (
            <div style={{ ...card, padding: '22px', fontSize: '12.5px', color: C.muted, textAlign: 'center' }}>No pending requests.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {pending.map(r => (
                <div key={r.id} style={{ ...card, padding: '16px 18px' }}>
                  <div style={{ fontSize: '13.5px', fontWeight: 600, color: C.navy, marginBottom: '2px' }}>{r.manager_firm?.name || 'A firm'}</div>
                  <div style={{ fontSize: '12px', color: C.textSec, marginBottom: '2px' }}>Hiring for: {r.job?.title || 'a role'}</div>
                  <div style={{ fontSize: '11.5px', color: C.muted, marginBottom: '12px' }}>Requested {fmtDate(r.requested_at)}</div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => respond(r.id, true)} disabled={busy === r.id} style={{ ...btnPrimary, opacity: busy === r.id ? 0.7 : 1 }}>{busy === r.id ? '…' : 'Accept'}</button>
                    <button onClick={() => respond(r.id, false)} disabled={busy === r.id} style={{ ...btnGhost, color: C.danger, borderColor: `${C.danger}44` }}>Decline</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div style={{ ...labelStyle, marginBottom: '12px' }}>Your Applications ({applied.length})</div>
          {applied.length === 0 ? (
            <div style={{ ...card, padding: '22px', fontSize: '12.5px', color: C.muted, textAlign: 'center' }}>
              You haven't applied to any roles yet. Browse the Job Board to express interest.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {applied.map(r => (
                <div key={r.id} style={{ ...card, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: C.text }}>{r.job?.title || 'a role'}</div>
                    <div style={{ fontSize: '11.5px', color: C.muted }}>{r.manager_firm?.name || 'A firm'} · Applied {fmtDate(r.requested_at)}</div>
                  </div>
                  <StatusBadge status={r.status} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div style={{ ...labelStyle, marginBottom: '12px' }}>Accepted Connections ({accepted.length})</div>
          {accepted.length === 0 ? (
            <div style={{ ...card, padding: '22px', fontSize: '12.5px', color: C.muted, textAlign: 'center' }}>No accepted connections yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {accepted.map(r => {
                const on = active?.id === r.id;
                return (
                  <div key={r.id} onClick={() => setActive(r)} style={{ ...card, padding: '14px 18px', cursor: 'pointer', borderLeft: on ? `3px solid ${C.navy}` : `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: C.text }}>{r.manager_firm?.name || 'A firm'}</div>
                      <div style={{ fontSize: '11.5px', color: C.muted }}>{r.job?.title || 'a role'}</div>
                    </div>
                    <span style={{ fontSize: '11.5px', color: C.navy, fontWeight: 600 }}>{on ? 'Open ▸' : 'Message'}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {active && (
        <div style={{ flex: '1 1 420px', minWidth: '360px', position: 'sticky', top: '20px' }}>
          <MessageThread connection={active} userId={userId} title={active.manager_firm?.name || 'Firm'} subtitle={active.job?.title || ''} />
        </div>
      )}
    </div>
  );
}

// Reusable message thread, used by candidate and manager sides.
export function MessageThread({ connection, userId, title, subtitle }: {
  connection: TalentConnection; userId: string; title: string; subtitle?: string;
}) {
  const [messages, setMessages] = useState<TalentMessage[]>([]);
  const [newMsg, setNewMsg] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('talent_messages').select('*').eq('connection_id', connection.id).order('created_at', { ascending: true });
      if (cancelled) return;
      setMessages((data as TalentMessage[]) || []);
      await supabase.from('talent_messages').update({ read_at: new Date().toISOString() }).eq('connection_id', connection.id).neq('sender_id', userId).is('read_at', null);
    })();
    const channel = supabase.channel(`tc-msg-${connection.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'talent_messages', filter: `connection_id=eq.${connection.id}` },
        async payload => {
          const m = payload.new as TalentMessage;
          setMessages(prev => prev.some(x => x.id === m.id) ? prev : [...prev, m]);
          if (m.sender_id !== userId) await supabase.from('talent_messages').update({ read_at: new Date().toISOString() }).eq('id', m.id);
        })
      .subscribe();
    return () => { cancelled = true; supabase.removeChannel(channel); };
  }, [connection.id, userId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    if (!newMsg.trim()) return;
    const content = newMsg.trim(); setNewMsg('');
    const { data } = await supabase.from('talent_messages').insert({ connection_id: connection.id, sender_id: userId, content }).select().single();
    if (data) setMessages(prev => prev.some(x => x.id === (data as TalentMessage).id) ? prev : [...prev, data as TalentMessage]);
  };

  const fmt = (ts: string) => new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  return (
    <div style={{ ...card, display: 'flex', flexDirection: 'column', height: '560px', overflow: 'hidden' }}>
      <div style={{ padding: '13px 20px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: C.navy }}>{title}</div>
        {subtitle && <div style={{ fontSize: '11.5px', color: C.muted }}>{subtitle}</div>}
      </div>
      <div style={{ flex: 1, padding: '20px 22px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', background: C.surface }}>
        {messages.length === 0 && <div style={{ textAlign: 'center', color: C.muted, fontSize: '13px', marginTop: '40px' }}>No messages yet. Say hello.</div>}
        {messages.map(m => {
          const isMe = m.sender_id === userId;
          return (
            <div key={m.id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
              <div style={{ maxWidth: '75%', padding: '10px 13px', borderRadius: '4px', background: isMe ? C.navy : C.white, color: isMe ? C.white : C.text, fontSize: '13px', lineHeight: 1.55, border: isMe ? 'none' : `1px solid ${C.border}`, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                <div>{m.content}</div>
                <div style={{ fontSize: '10px', marginTop: '4px', opacity: 0.55, textAlign: 'right', fontFamily: '"Courier New", monospace' }}>{fmt(m.created_at)}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div style={{ padding: '12px 18px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '8px' }}>
        <input value={newMsg} onChange={e => setNewMsg(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()} placeholder="Type a message…" style={{ ...inputStyle, background: C.surface }} />
        <button onClick={send} style={{ ...btnPrimary, padding: '9px 18px' }}>Send</button>
      </div>
    </div>
  );
}

// ── Settings tab ─────────────────────────────────────────────────────────────
function SettingsTab({ candidate, onSaved }: { candidate: Candidate; onSaved: (c: Candidate) => void }) {
  const [visible, setVisible] = useState(candidate.visible_on_platform);
  const [hideEmp, setHideEmp] = useState(candidate.hide_employer);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState<string | null>(null);

  const save = async (patch: Partial<Candidate>) => {
    setSaving(true); setMsg(null);
    const { data, error } = await supabase.from('candidates').update({ ...patch, updated_at: new Date().toISOString() }).eq('id', candidate.id).select().single();
    setSaving(false);
    if (!error && data) { onSaved(data as Candidate); setMsg('Settings saved.'); }
  };

  const Toggle = ({ on, onClick }: { on: boolean; onClick: () => void }) => (
    <button onClick={onClick} disabled={saving} style={{ width: '44px', height: '24px', borderRadius: '12px', border: 'none', background: on ? C.success : C.border, position: 'relative', cursor: 'pointer', flexShrink: 0, transition: 'background 0.15s' }}>
      <span style={{ position: 'absolute', top: '2px', left: on ? '22px' : '2px', width: '20px', height: '20px', borderRadius: '50%', background: C.white, transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }} />
    </button>
  );

  return (
    <div style={{ ...card, padding: '26px 28px', maxWidth: '680px' }}>
      {msg && <Banner type="ok" text={msg} />}

      <div style={{
        background: visible ? '#F0FDF4' : '#FEFCE8',
        border: `1px solid ${visible ? '#BBF7D0' : '#FDE68A'}`,
        borderRadius: '3px', padding: '11px 14px', marginBottom: '20px',
        fontSize: '12.5px', color: visible ? C.success : C.amber, fontWeight: 600,
      }}>
        Your profile is currently {visible ? 'visible' : 'hidden'} to managers
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', paddingBottom: '20px', borderBottom: `1px solid ${C.borderLt}`, marginBottom: '20px' }}>
        <div>
          <div style={{ fontSize: '14px', fontWeight: 600, color: C.text, marginBottom: '4px' }}>Visible on platform</div>
          <div style={{ fontSize: '12.5px', color: C.muted, lineHeight: 1.6, maxWidth: '440px' }}>
            Controls whether your profile appears in manager searches on TalentConnect.
          </div>
          {!visible && (
            <div style={{ marginTop: '10px', background: '#FEFCE8', border: '1px solid #FDE68A', borderRadius: '3px', padding: '9px 12px', fontSize: '12px', color: C.amber, lineHeight: 1.5, maxWidth: '440px' }}>
              Your profile will not appear in manager searches. You can still be flagged to specific roles by the Altveno team.
            </div>
          )}
        </div>
        <Toggle on={visible} onClick={() => { const v = !visible; setVisible(v); save({ visible_on_platform: v }); }} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '14px', fontWeight: 600, color: C.text, marginBottom: '4px' }}>Hide current employer</div>
          <div style={{ fontSize: '12.5px', color: C.muted, lineHeight: 1.6, maxWidth: '440px' }}>
            When on, managers see your current role but not the name of your current employer.
          </div>
        </div>
        <Toggle on={hideEmp} onClick={() => { const v = !hideEmp; setHideEmp(v); save({ hide_employer: v }); }} />
      </div>

      {/* Notification preferences are not wired to a backend yet, the toggles
          are disabled so they cannot imply a setting that is being stored. */}
      <div style={{ borderTop: `1px solid ${C.borderLt}`, marginTop: '20px', paddingTop: '20px' }}>
        <div style={{ ...labelStyle, marginBottom: '4px' }}>Notifications</div>
        <div style={{ fontSize: '12px', color: C.mutedLt, marginBottom: '16px' }}>Coming soon, these preferences are not saved yet.</div>
        {[
          ['Email me about new connection requests', true],
          ['Email me when a manager replies', true],
          ['Email me about new roles matching my profile', false],
        ].map(([label, on]) => (
          <div key={label as string} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '12px', opacity: 0.55 }}>
            <div style={{ fontSize: '13px', color: C.textSec }}>{label}</div>
            <button disabled style={{ width: '44px', height: '24px', borderRadius: '12px', border: 'none', background: on ? C.success : C.border, position: 'relative', cursor: 'not-allowed', flexShrink: 0 }}>
              <span style={{ position: 'absolute', top: '2px', left: on ? '22px' : '2px', width: '20px', height: '20px', borderRadius: '50%', background: C.white, boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
